import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowLeft, DownloadSimple, File, Eye, PaperclipHorizontal } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContract = () => {
    setLoading(true);
    api.get(`/contracts/${id}`).then(r => setContract(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchContract(); }, [id]);

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/contracts/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully');
      fetchContract();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-64 bg-muted rounded-sm" /><div className="h-64 bg-muted rounded-sm" /></div>;
  if (!contract) return <div className="text-center py-12 text-[#475569]">Contract not found</div>;

  const canUpload = user?.role === 'ap_accounting';
  const docUrl = contract.file_path ? `${API_BASE}/api/files/${contract.file_path}` : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="contract-detail-page">
      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft size={16} className="mr-1" /> Back
      </Button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{contract.contract_code}</h1>
          <p className="text-sm text-[#475569] mt-1">{contract.title}</p>
        </div>
        <span className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.05em] font-bold rounded-sm ${
          contract.status === 'active' ? 'bg-[#DCFCE7] text-[#166534] border border-[#22C55E]' :
          contract.status === 'archived' ? 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]' :
          'bg-[#FEF9C3] text-[#854D0E] border border-[#EAB308]'
        }`}>{contract.status}</span>
      </div>

      {/* Source Document — always prominent */}
      <Card className="rounded-sm border" data-testid="source-document-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <File size={16} className="text-[#0055FF]" /> Source Document
          </CardTitle>
        </CardHeader>
        <CardContent>
          {docUrl ? (
            <div className="flex items-center justify-between py-3 px-4 bg-[#F1F5F9] rounded-sm border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#0055FF]/10 flex items-center justify-center">
                  <File size={22} className="text-[#0055FF]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">{contract.file_name || 'Contract Document'}</p>
                  <p className="text-xs text-[#94A3B8]">Original uploaded document</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#0055FF] bg-white border border-[#0055FF]/30 rounded-sm hover:bg-[#0055FF]/5 transition-colors"
                  data-testid="view-document-btn"
                >
                  <Eye size={14} /> View
                </a>
                <a
                  href={docUrl}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#0055FF] rounded-sm hover:bg-[#0043CC] transition-colors"
                  data-testid="download-document-btn"
                >
                  <DownloadSimple size={14} /> Download
                </a>
              </div>
            </div>
          ) : (
            <div className="py-6 text-center border-2 border-dashed border-[#E2E8F0] rounded-sm">
              <File size={32} className="mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#475569]">No contract document uploaded</p>
              {canUpload && (
                <>
                  <Button variant="outline" size="sm" className="rounded-sm mt-3 gap-2" onClick={() => fileRef.current?.click()} data-testid="upload-document-btn">
                    <PaperclipHorizontal size={14} /> Upload Document
                  </Button>
                  <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleUploadDoc} />
                </>
              )}
            </div>
          )}
          {docUrl && canUpload && (
            <div className="mt-2 flex justify-end">
              <Button variant="ghost" size="sm" className="rounded-sm text-xs text-[#475569] gap-1" onClick={() => fileRef.current?.click()} data-testid="replace-document-btn">
                <PaperclipHorizontal size={12} /> Replace Document
              </Button>
              <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleUploadDoc} />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract Information */}
        <Card className="rounded-sm border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Contract Information</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Vendor</span><Link to={`/vendors/${contract.vendor_id}`} className="text-[#0055FF] hover:underline">{contract.vendor_name}</Link></div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Type</span>{contract.contract_type || '-'}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Start Date</span>{contract.start_date || '-'}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">End Date</span>{contract.end_date || '-'}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Contract Amount</span><span className="text-lg font-bold">{fmt(contract.contract_amount)}</span></div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Renewal Terms</span>{contract.renewal_terms || '-'}</div>
            </div>
            {contract.notes && <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Notes</span>{contract.notes}</div>}
          </CardContent>
        </Card>

        {/* Recurring Obligations */}
        {contract.is_recurring ? (
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recurring Obligations</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Recurring Amount</span><span className="text-lg font-bold">{fmt(contract.recurring_amount)}</span></div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Frequency</span>{contract.recurring_frequency}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Next Payment</span>{contract.next_payment_date || '-'}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Recurring End</span>{contract.recurring_end_date || '-'}</div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Timestamps</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Created</span>{contract.created_at?.slice(0, 10)}</div>
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Last Updated</span>{contract.updated_at?.slice(0, 10)}</div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Linked Payments */}
      <Card className="rounded-sm border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Linked Payment Requests ({contract.linked_payments?.length || 0})</CardTitle></CardHeader>
        <CardContent className={contract.linked_payments?.length > 0 ? "p-0" : ""}>
          {contract.linked_payments?.length > 0 ? (
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Category</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Status</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {contract.linked_payments.map(p => (
                  <TableRow key={p.id}><TableCell><Link to={`/payments/${p.id}`} className="text-[#0055FF] text-sm">{p.request_code}</Link></TableCell><TableCell className="text-sm">{p.category}</TableCell><TableCell className="text-right text-sm">{fmt(p.amount)}</TableCell><TableCell className="text-sm">{p.status}</TableCell><TableCell className="text-sm">{p.created_at?.slice(0, 10)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-[#94A3B8] text-center py-4">No payment requests linked to this contract</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
